import { Component, computed, effect, ElementRef, inject, Injector, OnDestroy, signal, viewChild } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { AlertsMarqueeService } from './alerts-marquee.service';
import { nextClockDelayMs } from './clock-tick.util';
import { ClockWidgetService } from '../usa-timezone-clock/clock-widget.service';
import { AlertEntity, AlertsMarqueeItemComponent } from './alerts-marquee-item.component';
import { ButtonComponentComponent } from '../button-component/button-component.component';
import { PopupComponent } from '../popup/popup.component';
import { TrailerCompactDetailComponent } from '../../modules/assets/trailer/trailer-compact-detail/trailer-compact-detail.component';
import { TruckCompactDetailComponent } from '../../modules/assets/truck/truck-compact-detail/truck-compact-detail.component';
import { DriverCompactDetailComponent } from '../../modules/assets/driver/driver-compact-detail/driver-compact-detail.component';
import { LoadService } from '../../modules/dispatch/load/loadServices/load.service';

const BAR_HEIGHT = '2.5rem';
const REFRESH_INTERVAL_MS = 3 * 60 * 1000;
const SCROLL_PX_PER_SEC = 60;

const ASSET_POPUP_HEADING: Record<Exclude<AlertEntity, 'load' | 'dispatch'>, string> = {
  trailer: 'Trailer Compact Details',
  truck: 'Truck Compact Details',
  driver: 'Driver Compact Details',
};

@Component({
  selector: 'app-alerts-marquee',
  standalone: true,
  imports: [CommonModule, AlertsMarqueeItemComponent, ButtonComponentComponent, PopupComponent, TrailerCompactDetailComponent, TruckCompactDetailComponent, DriverCompactDetailComponent],
  templateUrl: './alerts-marquee.component.html',
  styleUrl: './alerts-marquee.component.css',
})
export class AlertsMarqueeComponent implements OnDestroy {
  private alertsService = inject(AlertsMarqueeService);
  private injector = inject(Injector);
  private doc = inject(DOCUMENT);
  readonly clockWidget = inject(ClockWidgetService);
  private readonly marqueeGroup = viewChild<ElementRef<HTMLElement>>('marqueeGroup');
  private groupObserver?: ResizeObserver;

  readonly assetPopup = signal<{ entity: Exclude<AlertEntity, 'load' | 'dispatch'>; heading: string; data: { id: number } } | null>(null);
  readonly loadPopup = signal<{ heading: string; loadId: number } | null>(null);
  readonly loadDetailsComp = signal<any>(null);
  readonly loadDetailsInjector = signal<Injector | undefined>(undefined);
  readonly dispatchPopup = signal<{ heading: string; dispatchId: number } | null>(null);
  readonly dispatchDetailsComp = signal<any>(null);
  readonly dispatchDetailsInjector = signal<Injector | undefined>(undefined);

  readonly alerts = this.alertsService.alerts;
  readonly totalCount = this.alertsService.totalCount;
  readonly lastUpdated = this.alertsService.lastUpdated;
  readonly loading = this.alertsService.loading;
  readonly paused = signal(false);
  readonly tabHidden = signal(false);
  private readonly now = signal(new Date());
  readonly groupWidth = signal(0);
  private clockTimer: any;
  private pollTimer: any;

  readonly scrollDuration = computed(() => {
    const w = this.groupWidth();
    if (w > 0) return `${Math.max(4, w / SCROLL_PX_PER_SEC)}s`;
    return `${Math.max(65, this.alerts().length * 12)}s`;
  });

  readonly lastRefreshedLabel = computed(() => {
    const last = this.lastUpdated();
    if (!last) return '';
    const diff = Math.max(0, this.now().getTime() - last);
    const sec = Math.floor(diff / 1000);
    if (sec < 10) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    return `${hr} hr ago`;
  });

  constructor() {
    this.alertsService.loadAlerts();
    effect(() => {
      this.doc.documentElement.style.setProperty('--app-bottom-bar', this.alerts().length || this.clockWidget.enabled() ? BAR_HEIGHT : '0rem');
    });
    effect(() => this.observeGroup(this.marqueeGroup()?.nativeElement));
    this.pollTimer = setInterval(() => this.alertsService.loadAlerts(), REFRESH_INTERVAL_MS);
    this.tabHidden.set(this.doc.visibilityState === 'hidden');
    this.doc.addEventListener('visibilitychange', this.onVisibilityChange);
    this.scheduleClockTick();
  }

  private observeGroup(group?: HTMLElement): void {
    this.groupObserver?.disconnect();
    if (!group || typeof ResizeObserver === 'undefined') return;
    this.groupObserver = new ResizeObserver(() => {
      const w = group.offsetWidth;
      if (w && Math.abs(w - this.groupWidth()) > 1) this.groupWidth.set(w);
    });
    this.groupObserver.observe(group);
  }

  private readonly onVisibilityChange = (): void => {
    this.tabHidden.set(this.doc.visibilityState === 'hidden');
    this.scheduleClockTick();
  };

  private scheduleClockTick(): void {
    if (this.clockTimer) clearTimeout(this.clockTimer);
    this.clockTimer = null;
    if (this.tabHidden()) return;
    const current = new Date();
    this.now.set(current);
    this.clockWidget.updateNow(current);
    this.clockTimer = setTimeout(() => this.scheduleClockTick(), nextClockDelayMs(current.getTime(), this.lastUpdated() || null));
  }

  ngOnDestroy(): void {
    if (this.clockTimer) clearTimeout(this.clockTimer);
    if (this.pollTimer) clearInterval(this.pollTimer);
    this.groupObserver?.disconnect();
    this.doc.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.doc.documentElement.style.setProperty('--app-bottom-bar', '0rem');
  }

  togglePause(): void {
    this.paused.update((v) => !v);
  }

  refresh(): void {
    if (this.loading()) return;
    this.alertsService.loadAlerts();
  }

  handleOpenEntity(target: { entity: AlertEntity; id: number }): void {
    if (!target?.id) return;
    if (target.entity === 'load') {
      this.openLoad(target.id);
      return;
    }
    if (target.entity === 'dispatch') {
      this.openDispatch(target.id);
      return;
    }
    this.assetPopup.set({
      entity: target.entity,
      heading: ASSET_POPUP_HEADING[target.entity],
      data: { id: target.id },
    });
  }

  closeAssetPopup(): void {
    this.assetPopup.set(null);
  }

  openLoad(id: number): void {
    if (!id) return;
    this.loadDetailsInjector.set(Injector.create({ providers: [LoadService], parent: this.injector }));
    if (!this.loadDetailsComp()) {
      import('../../modules/dispatch/load/load-details/load-details.component').then((m) => this.loadDetailsComp.set(m.LoadDetailsComponent));
    }
    this.loadPopup.set({ heading: 'Load Details', loadId: id });
  }

  closeLoadPopup(): void {
    this.loadPopup.set(null);
  }

  openDispatch(id: number): void {
    if (!id) return;
    this.dispatchDetailsInjector.set(Injector.create({ providers: [LoadService], parent: this.injector }));
    if (!this.dispatchDetailsComp()) {
      import('../../modules/dispatch/running/dispatch-details/dispatch-details.component').then((m) => this.dispatchDetailsComp.set(m.DispatchDetailsComponent));
    }
    this.dispatchPopup.set({ heading: 'Dispatch Details', dispatchId: id });
  }

  closeDispatchPopup(): void {
    this.dispatchPopup.set(null);
  }
}
